import moment from 'moment';
import { humanise } from 'components/atoms/humanised-date';
import { TranslationKey, useTranslate } from 'hooks/use-translation';
import { CompanyAllocationSummaryEvent } from '../../event-types';
import { getTotalAllocatedMinutes } from '../../utils/common';
import { ignoreMinutesIfZero } from '../../utils/dates';
import { ViewPresetId } from '../view-presets/constants';

type Props = Pick<
  CompanyAllocationSummaryEvent,
  'allocatedMinutes' | 'id' | 'forViewPreset'
>;

const getText = (
  allocatedMinutes: number,
  forViewPreset: ViewPresetId,
): string =>
  allocatedMinutes > 0
    ? `${ignoreMinutesIfZero(allocatedMinutes)}${
        forViewPreset === ViewPresetId.Day ||
        forViewPreset === ViewPresetId.DayZoomed
          ? '/d'
          : ''
      }`
    : '';

export const companySummaryTemplate = ({
  allocatedMinutes,
  id,
  forViewPreset,
}: Props): string => {
  return `<div class='content' data-event-id=${id}>${getText(
    getTotalAllocatedMinutes(allocatedMinutes),
    forViewPreset,
  )}</div>`;
};

type TooltipProps = Pick<
  CompanyAllocationSummaryEvent,
  'allocatedMinutes' | 'startDate' | 'endDate' | 'forViewPreset'
> & {
  name?: string;
};
export const companySummaryTooltip = (
  { allocatedMinutes, name, startDate, endDate, forViewPreset }: TooltipProps,
  translate: ReturnType<typeof useTranslate>,
): string => {
  const startDateMoment = moment(startDate);
  const endDateMoment = moment(endDate);
  const startDateString = humanise(startDateMoment);
  const endDateString = humanise(endDateMoment);
  const isSingleDay = startDateMoment.isSame(endDateMoment, 'day');

  return translate(
    'resource_management.company_summary.tooltip' as TranslationKey,
    {
      name,
      startDate: startDateString,
      endDate: endDateString,
      time: `${ignoreMinutesIfZero(
        Math.abs(getTotalAllocatedMinutes(allocatedMinutes)),
      )}${
        forViewPreset === ViewPresetId.Day ||
        forViewPreset === ViewPresetId.DayZoomed
          ? '/d'
          : ''
      }`,
      hasDates: isSingleDay ? 'singleDay' : false,
      strong: (chunks) => `<strong>${chunks}</strong>`,
    },
  );
};
